import type { PowerCoolingInputs } from '../findings/powerCoolingAnswers';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { deriveIntentForPlanningReport } from '../generationImpact/selectedAssessmentIntent';
import { readGridLedStandbyPlanningReportProjection } from '../generationImpact/gridLedStandbyPlanningReportContent';

/** Frozen adapter. A later live SED is never accepted as an historical snapshot.
 * Existing revisions do not universally persist historical SED bytes; null then
 * leaves helper-only measurements unknown without reconstructing a canonical SED. */
export function powerCoolingInputsAtGeneration(report: Omit<SiteRagReport, 'decisionAnalysis'>, _unverifiedCandidate: SiteEvidenceDocument | null = null): PowerCoolingInputs {
  // ID/version identify a SED but do not prove its archived bytes. Reports do
  // not yet carry a universal hash-bound SED snapshot, so an externally supplied
  // candidate cannot become historical authority, even when the version matches.
  const sed: SiteEvidenceDocument | null = null;
  // Inspect the original frozen marker before any presentation allowlist can
  // hide it. The private snapshot never supplies a display fallback.
  readGridLedStandbyPlanningReportProjection(report);
  const intent = deriveIntentForPlanningReport(report, report.projectIntentAtGen ?? null);
  const acreage = report.screeningBasis?.studyArea?.grossAreaAcres ?? null;
  return {
    site: { id: report.siteId, name: report.siteName, location: report.location,
      projectIntent: intent, typedPreferencesDefaults: report.typedPreferencesAtGen ?? {},
      siteBoundaryAggregate: report.siteBoundaryAggregate } as unknown as SiteEntity,
    sed, feasibility: report, environmental: null, promotedScenario: null,
    availability: null,
    studyAreaAcres: acreage, asOf: String(report.createdAt).slice(0, 10),
    trusted: { waterStress: null, flood: null, seismic: null },
    dryCapacity: null,
    stage: 'early-screening',
  };
}
